import {
    Injectable,
    WritableSignal,
    Signal,
    signal,
    inject,
  } from '@angular/core';
  import { ApiService } from '../services/api-services/api.service';
  import { catchError, finalize, of } from 'rxjs';
  import { CalendarEvent, Place } from '../interfaces/interfaces';
import { BookingsStore } from './bookings.store';
  export interface ColleagueLocationResult {
    colleagueUpn: string;
    locations: CalendarEvent[];
    parking: CalendarEvent;
  }
  @Injectable({
    providedIn: 'root',
  })
  export class ColleaguesStore {
    // ========================
    // Signals
    // ========================
  
    private _colleaguesSignal: WritableSignal<ColleagueLocationResult[] | null> =
      signal<ColleagueLocationResult[] | null>(null);
  
    private _loadingSignal = signal<boolean>(false);
    private _errorSignal = signal<string | null>(null);
  
    // ========================
    // Readonly Signals
    // ========================
  
    public colleagues: Signal<ColleagueLocationResult[] | null> =
      this._colleaguesSignal.asReadonly();
  
    public loading: Signal<boolean> = this._loadingSignal.asReadonly();
    public error: Signal<string | null> = this._errorSignal.asReadonly();
  
    // ========================
    // Dependencies
    // ========================
  
    private apiService = inject(ApiService);
    private bookingsStore = inject(BookingsStore)
  
    // ========================
    // Actions
    // ========================
  
    fetchColleagues(attendees: string[]): void {
      
      if (!attendees || attendees.length === 0) {
        this._colleaguesSignal.set(null);
        return;
      }
  
      this._loadingSignal.set(true);
      this._errorSignal.set(null);
  
      this.apiService.findColleague(attendees)
        .pipe(
          finalize(() => this._loadingSignal.set(false)),
          catchError(error => {
            this._errorSignal.set(
              error?.message || 'Failed to fetch colleagues'
            );
            return of(null);
          })
        )
        .subscribe((res: ColleagueLocationResult[] | null) => {
          // Safety guard
          res ??= [];
          res.forEach(colleagueLocationResult => {
            if (colleagueLocationResult.locations != null) {
              this.bookingsStore.mapBookingsToAvatars(colleagueLocationResult.locations)
              this.bookingsStore.mapBookingsToFloorplans(colleagueLocationResult.locations)
            }
            if (colleagueLocationResult.parking != null) {
              this.bookingsStore.mapBookingsToAvatars([colleagueLocationResult.parking])
              this.bookingsStore.mapBookingsToFloorplans([colleagueLocationResult.parking])
            }
          })
          this._colleaguesSignal.update(existing => {
            const current = existing ?? [];
        
            return [
              ...current,
              ...res.filter(
                r => !current.some(c => c.colleagueUpn === r.colleagueUpn)
              )
            ];
          });
        });
    }
  
    reset() {
      this._colleaguesSignal.set(null);
      this._loadingSignal.set(false);
      this._errorSignal.set(null);
    }
  }
  